import { useCallback, useEffect, useState } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { PetitionEditPageState } from 'src/app/pages/petitionEditV2/pageContext';
import { usePetitionEditPageState } from 'src/app/pages/petitionEditV2/pageContext';
import type { RawMediaCropInput, UploaderMethodProps } from 'src/app/shared/components/imageUpload';
import { UPLOAD_ERROR_MESSAGES, useUploader } from 'src/app/shared/hooks/uppyUploader';

import { validateImageSize } from './utils';

type FormPhoto = PetitionEditPageState['formState']['media']['value'];
type Data = { image?: string; error: TranslationKey | null; media: FormPhoto; uploader: UploaderMethodProps };
type Actions = {
	handleImageChange: (image?: string) => void;
	handleImageCrop: (croppingData?: RawMediaCropInput) => void;
	handleRemoveMedia: () => void;
	handleCancelUpload: () => void;
};
type UseMediaReturn = ModelHookResult<Data, Actions>;

export function useMedia(): UseMediaReturn {
	const [state, mutations] = usePetitionEditPageState();
	const media = state.formState.media.value;

	// this mutation is for removing media that is already on the petition
	const removeMedia = useCallback(() => {
		mutations.removeMedia();
	}, [mutations]);

	/**
	 * Everything below this is related to the `uploader`
	 * `uploader` state is stored locally (and internal to `uploader`)
	 * Because you need the `uploader` instance to upload the cropped image,
	 * there is no need to store image or crop data in page state.
	 *
	 * Additionally, since the PUT api temporarily only supports `img_src` (url) as input
	 * we handle uploading the cropped image during submission of the petition edit form,
	 * prior to sending the PUT api request, hence its storage in page state.
	 */
	const utilityContext = useUtilityContext();
	const [imageData, setImageData] = useState<string>();
	const [imageError, setImageError] = useState<TranslationKey | null>(null);
	const uploader = useUploader(setImageError);
	const onImageChange = useCallback(
		(image?: string) => {
			if (!image) return;

			setImageError(null);
			setImageData(image);
			// store `uploader` in state so that the PUT api can call this as part of the form submission
			// its presence is used as a heuristic to determine whether to call `uploader.upload` during form submit
			mutations.setUploader(uploader);
		},
		[mutations, uploader],
	);

	const onImageCrop = useCallback((croppingData?: RawMediaCropInput) => {
		if (!croppingData) return;
		// this checks the image size to suggest using a larger image if the user uploads a small image
		// its not a restriction like the errors handled in `uppy`
		const isValidImageSize = validateImageSize(croppingData);
		if (!isValidImageSize) setImageError(UPLOAD_ERROR_MESSAGES.IMAGE_TOO_SMALL);
	}, []);

	const cancelUpload = useCallback(() => {
		setImageError(null);
		setImageData(undefined);
		// remove the uploader if the user cancels uploading an image
		// its presence is used as a heuristic to determine whether to call `uploader.upload` during form submit
		mutations.setUploader(null);
	}, [mutations]);

	useEffect(() => {
		uploader.onFileAdded(onImageChange);
		uploader.onUseDropTarget();
		uploader.onUseAwsS3(utilityContext, 'USER_UPLOADS');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		data: { image: imageData, error: imageError, media, uploader },
		actions: {
			handleImageChange: onImageChange,
			handleImageCrop: onImageCrop,
			handleRemoveMedia: removeMedia,
			handleCancelUpload: cancelUpload,
		},
	};
}
